import { AccountLevelEnum, ButtonTypeEnum } from '@app/app.enum';
import { history } from '@app/app.routes';
import CustomButton from '@app/components/shared/custom-button.component';
import CustomTooltip, { ICustomTooltipTitleitems } from '@app/components/shared/custom-tooltip.component';
import CustomDialogBox from '@app/components/shared/dialog.component';
import Icon from '@app/components/shared/icon.component';
import LabelInput from '@app/components/shared/label-input.component';
import { AddNewUSPSTierRoutedFromTierDropDown } from '@app/constants';
import environment from '@app/environments/environment';
import * as models from '@app/models';
import { IAccount, ICustomerAccountDetailsState } from '@app/models';
import injector from '@app/services';
import { IntlService } from '@app/services/intl.service';
import { Box, Chip, CircularProgress, Divider, Grid, MenuItem, Select, TextField, Typography } from '@material-ui/core';
import React, { useState } from 'react';

const intlService: IntlService = injector.get(IntlService);

const AdminAccountUSPSTierComponent: React.FC<IAdminAccountTierAndOverrideRateSetProps> = (props) => {
	const [isUpdateTierDialogOpen, setIsUpdateTierDialogOpen] = useState<boolean>(false);

	const getUSPSConfigComponentForPartner = (): JSX.Element => {
		return (
			<>
				<Grid container={true} direction='column' spacing={3}>
					<Grid item={true}>
						<Grid container={true} spacing={4}>
							<Grid item={true} xs={6}>
								<LabelInput
									label={intlService.GetFormattedMessage('admin.account-details.partner-new-tier-label')}
									renderInput={renderTierConfigDropdownMenu}
									renderTooltip={() =>
										generateTierToolTip({
											currentTier: props.uspsTierConfig.current,
											selectedTier: props.uspsTierConfig.selected,
											isPartnerTier: true,
										})
									}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item={true} className='text-center'>
						<CustomButton
							type={ButtonTypeEnum.Button}
							buttonText={intlService.GetFormattedMessage('app.billing.auto-buy-update-button-text')}
							loading={props.buttonLoading}
							disabled={disableButton()}
							onClick={() => handleUSPSTierUpdate()}
						/>
					</Grid>
				</Grid>
			</>
		);
	};

	const getUSPSConfigComponentForCustomer = (): JSX.Element => {
		return (
			<Grid container={true} spacing={3}>
				<Grid item={true} xs={12}>
					{props.uspsConfigdataLoading ? (
						<div className='spinner-for-config'>
							<CircularProgress size={24} />
						</div>
					) : (
						getConfigByRatingSource()
					)}
				</Grid>
			</Grid>
		);
	};

	const getConfigByRatingSource = () => {
		return (
			<>
				<Grid container={true} direction='column' spacing={2}>
					<Grid item={true} xs={6}>
						<Grid container={true} direction='column' spacing={2}>
							{props.account.level === AccountLevelEnum.Customer ? (
								<Grid item={true} xs={12}>
									<LabelInput
										label={intlService.GetFormattedMessage('admin.account-details.partner-tier-label')}
										renderInput={() => (
											<TextField disabled={true} fullWidth={true} value={props.partnerUSPSTier?.tier ?? 'None'} />
										)}
										renderTooltip={() =>
											generateTierToolTip({
												currentTier: props.partnerUSPSTier,
												selectedTier: props.partnerUSPSTier,
												isPartnerTier: true,
											})
										}
									/>
								</Grid>
							) : null}
							<Grid item={true} xs={12}>
								<LabelInput
									label={intlService.GetFormattedMessage('usps-tier-config.tier-config-label')}
									renderInput={renderTierConfigDropdownMenu}
									renderTooltip={() =>
										generateTierToolTip({
											currentTier: props.uspsTierConfig.current,
											selectedTier: props.uspsTierConfig.selected,
										})
									}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item={true} xs={12}>
						<Grid container={true} justifyContent='center' alignItems='center' className='text-center'>
							<Grid item={true} xs={12}>
								{/* Button component */}
								{!props.isPartnerAccount && (
									<CustomButton
										disabled={disableButton()}
										onClick={() => handleUSPSTierUpdate()}
										buttonText={intlService.GetFormattedMessage('app.billing.auto-buy-update-button-text')}
										loading={props.buttonLoading}
									/>
								)}
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</>
		);
	};

	const renderTierConfigDropdownMenu = (): JSX.Element => {
		return (
			<Select
				fullWidth={true}
				displayEmpty={true}
				disabled={props.isPartnerAccount}
				value={props.uspsTierConfig.selected?.id || 'default'}
				onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
					const selectedConfig = props.uspsTierConfigs.find((x) => x.id === e.target.value);
					if (selectedConfig.tier === models.AddNewUSPSTierData().tier) {
						history.push('/settings', {
							extraParams: { isRoutedFrom: AddNewUSPSTierRoutedFromTierDropDown },
						});
					}
					props.onSetUSPSTierConfig(selectedConfig);
				}}
				renderValue={(selectTierId: string) => {
					if (selectTierId === 'default') {
						return intlService.GetFormattedMessage('admin-account-details.select-tier');
					}
					return renderTierConfigMenuDisplayValue(selectTierId);
				}}
			>
				{props.uspsTierConfigs
					.filter((x) => props.account.level === AccountLevelEnum.Partner || x.tier !== models.NoneUSPSTierData().tier)
					.map((e: models.IUSPSTierConfig) => {
						return (
							<MenuItem key={e.id} value={e.id}>
								{
									<>
										{e.tier !== models.AddNewUSPSTierData().tier && e.tier}
										{e.id === props.uspsTierConfig.current?.id && (
											<>
												<Box mx={7} />
												<Chip
													label={intlService.GetFormattedMessage('admin.account-details.current-chip-label')}
													color='primary'
													size='small'
												/>
											</>
										)}
										{e.tier === models.AddNewUSPSTierData().tier && (
											<>
												<Box mx={7} />
												<Icon name='plus' className='mr-1' style={{ transform: 'translateY(1px)' }} />
												{intlService.GetFormattedMessage('admin.account-details.add-new-tier-chip-label')}
											</>
										)}
									</>
								}
							</MenuItem>
						);
					})}
			</Select>
		);
	};

	const renderTierConfigMenuDisplayValue = (selectedTierConfigId: string) => {
		const selectedTierConfig = props.uspsTierConfigs.find((e) => e.id === selectedTierConfigId);
		const isSameAsCurrentTierConfig: boolean = selectedTierConfig?.id === props.uspsTierConfig.current?.id;
		const currentChip: React.ReactNode = isSameAsCurrentTierConfig ? (
			<Grid item={true} xs={4}>
				<Chip
					label={intlService.GetFormattedMessage('admin.account-details.current-chip-label')}
					color='primary'
					size='small'
				/>
			</Grid>
		) : null;
		return (
			<Grid container={true} alignItems='center'>
				<Grid item={true} xs={8}>
					{selectedTierConfig?.tier}
				</Grid>
				{currentChip}
			</Grid>
		);
	};

	const disableButton = () =>
		props.buttonLoading ||
		!(props.uspsTierConfig.selected?.id || '') ||
		props.uspsTierConfig.selected?.id === props.uspsTierConfig.current?.id;

	const handleUSPSTierUpdate = () => {
		setIsUpdateTierDialogOpen(true);
	};

	const generateTierToolTip = ({
		currentTier,
		selectedTier,
		isPartnerTier = false,
	}: {
		currentTier: models.IUSPSTierConfig;
		selectedTier: models.IUSPSTierConfig;
		isPartnerTier?: boolean;
	}) => {
		const isUSPSFulfillmentTier =
			currentTier?.tier.toLowerCase() === environment.USPSFulfillmentTierName?.toLowerCase();
		const isSelectedTierSame = currentTier?.id === selectedTier?.id;

		let toolTipItems: ICustomTooltipTitleitems[] = [];

		if (isUSPSFulfillmentTier) {
			toolTipItems.push({
				value: intlService.GetFormattedMessage(
					`usps-tier-config-tier-fulfillment-not-eligible-info${isPartnerTier ? '-partner' : ''}`
				),
			});
		}

		if (!isSelectedTierSame) {
			toolTipItems = toolTipItems.concat([
				{
					header: intlService.GetFormattedMessage('usps-tier-config.current-tier-label'),
				},
				{
					label: intlService.GetFormattedMessage('usps-tier-config.master-mid-label'),
					value: currentTier.masterMID,
				},
				{
					label: intlService.GetFormattedMessage('usps-tier-config.mail-owner-mid-label'),
					value: currentTier.mailOwnerMID || intlService.GetFormattedMessage('app.common.not-applicable-value'),
				},
				{
					label: intlService.GetFormattedMessage('usps-tier-config.eVS-WebtoolId-label'),
					value: currentTier.eVSWebToolId || intlService.GetFormattedMessage('app.common.not-applicable-value'),
				},
				{
					label: intlService.GetFormattedMessage('usps-tier-config.eVS-Permit-Number-label'),
					value: currentTier.eVSPermitNum || intlService.GetFormattedMessage('app.common.not-applicable-value'),
				},
				{
					label: intlService.GetFormattedMessage('usps-tier-config.eVS-MID-label'),
					value: currentTier?.eVSMID || intlService.GetFormattedMessage('app.common.not-applicable-value'),
				},
				{
					label: intlService.GetFormattedMessage('usps-tier-config.eVS-Returns-MID-label'),
					value: currentTier?.eVSReturnsMID || intlService.GetFormattedMessage('app.common.not-applicable-value'),
				},
				{
					label: intlService.GetFormattedMessage('usps-tier-config.ePostage-WebtoolId-label'),
					value: currentTier.ePostageWebToolId || intlService.GetFormattedMessage('app.common.not-applicable-value'),
				},
				{
					label: intlService.GetFormattedMessage('usps-tier-config.ePostage-Permit-Number-label'),
					value: currentTier.ePostagePermitNum || intlService.GetFormattedMessage('app.common.not-applicable-value'),
				},
				{
					label: intlService.GetFormattedMessage('usps-tier-config.ePostage-MID-label'),
					value: currentTier.ePostageMID || intlService.GetFormattedMessage('app.common.not-applicable-value'),
				},
				{
					label: intlService.GetFormattedMessage('usps-tier-config.ePostage-Returns-MID-label'),
					value: currentTier?.ePostageReturnsMID || intlService.GetFormattedMessage('app.common.not-applicable-value'),
				},
				{
					label: intlService.GetFormattedMessage('usps-tier-config.eVS-Webtool-MID-label'),
					value: currentTier?.eVSWebtoolMID || intlService.GetFormattedMessage('app.common.not-applicable-value'),
				},
				{
					label: intlService.GetFormattedMessage('usps-tier-config.ePostage-Webtool-MID-label'),
					value: currentTier?.ePostageWebtoolMID || intlService.GetFormattedMessage('app.common.not-applicable-value'),
				},
                {
					label: intlService.GetFormattedMessage('usps-tier-config.consumer-key-label'),
					value: currentTier?.consumerKey || intlService.GetFormattedMessage('app.common.not-applicable-value'),
				},
                {
					label: intlService.GetFormattedMessage('usps-tier-config.consumer-secret-label'),
					value: currentTier?.consumerSecret || intlService.GetFormattedMessage('app.common.not-applicable-value'),
				},
				{
					label: intlService.GetFormattedMessage('usps-tier-config.description-label'),
					value: currentTier?.description || intlService.GetFormattedMessage('app.common.not-applicable-value'),
				},
				{
					label: intlService.GetFormattedMessage('usps-tier-config.number-of-associated-accounts-label'),
					value: String(currentTier?.numberOfAssociatedAccounts),
				},
				{ onRender: () => <Divider component='br' /> },
			]);
		}

		toolTipItems = toolTipItems.concat([
			{
				header: isSelectedTierSame ? '' : intlService.GetFormattedMessage(`usps-tier-config.selected-tier-label`),
			},
			{
				label: intlService.GetFormattedMessage('usps-tier-config.master-mid-label'),
				value: selectedTier.masterMID,
			},
			{
				label: intlService.GetFormattedMessage('usps-tier-config.mail-owner-mid-label'),
				value: currentTier.mailOwnerMID || intlService.GetFormattedMessage('app.common.not-applicable-value'),
			},
			{
				label: intlService.GetFormattedMessage('usps-tier-config.eVS-WebtoolId-label'),
				value: selectedTier.eVSWebToolId || intlService.GetFormattedMessage('app.common.not-applicable-value'),
			},
			{
				label: intlService.GetFormattedMessage('usps-tier-config.eVS-Permit-Number-label'),
				value: selectedTier.eVSPermitNum || intlService.GetFormattedMessage('app.common.not-applicable-value'),
			},
			{
				label: intlService.GetFormattedMessage('usps-tier-config.eVS-MID-label'),
				value: selectedTier?.eVSMID || intlService.GetFormattedMessage('app.common.not-applicable-value'),
			},
			{
				label: intlService.GetFormattedMessage('usps-tier-config.eVS-Returns-MID-label'),
				value: selectedTier?.eVSReturnsMID || intlService.GetFormattedMessage('app.common.not-applicable-value'),
			},
			{
				label: intlService.GetFormattedMessage('usps-tier-config.ePostage-WebtoolId-label'),
				value: selectedTier.ePostageWebToolId || intlService.GetFormattedMessage('app.common.not-applicable-value'),
			},
			{
				label: intlService.GetFormattedMessage('usps-tier-config.ePostage-Permit-Number-label'),
				value: selectedTier.ePostagePermitNum || intlService.GetFormattedMessage('app.common.not-applicable-value'),
			},
			{
				label: intlService.GetFormattedMessage('usps-tier-config.ePostage-MID-label'),
				value: selectedTier.ePostageMID || intlService.GetFormattedMessage('app.common.not-applicable-value'),
			},
			{
				label: intlService.GetFormattedMessage('usps-tier-config.ePostage-Returns-MID-label'),
				value: selectedTier?.ePostageReturnsMID || intlService.GetFormattedMessage('app.common.not-applicable-value'),
			},
			{
				label: intlService.GetFormattedMessage('usps-tier-config.eVS-Webtool-MID-label'),
				value: selectedTier?.eVSWebtoolMID || intlService.GetFormattedMessage('app.common.not-applicable-value'),
			},
			{
				label: intlService.GetFormattedMessage('usps-tier-config.ePostage-Webtool-MID-label'),
				value: selectedTier?.ePostageWebtoolMID || intlService.GetFormattedMessage('app.common.not-applicable-value'),
			},
            {
				label: intlService.GetFormattedMessage('usps-tier-config.consumer-key-label'),
				value: selectedTier?.consumerKey || intlService.GetFormattedMessage('app.common.not-applicable-value'),
			},
            {
				label: intlService.GetFormattedMessage('usps-tier-config.consumer-secret-label'),
				value: selectedTier?.consumerSecret || intlService.GetFormattedMessage('app.common.not-applicable-value'),
			},
			{
				label: intlService.GetFormattedMessage('usps-tier-config.description-label'),
				value: selectedTier?.description || intlService.GetFormattedMessage('app.common.not-applicable-value'),
			},
			{
				label: intlService.GetFormattedMessage('usps-tier-config.number-of-associated-accounts-label'),
				value: String(selectedTier?.numberOfAssociatedAccounts),
			},
		]);

		return (
			<CustomTooltip items={toolTipItems} toolTipProps={{ disableHoverListener: !currentTier }} display={!!currentTier}>
				<Typography>
					<Icon name='info-circle' />
				</Typography>
			</CustomTooltip>
		);
	};

	return (
		<>
			{/* Tier Config Dialog Box */}
			<CustomDialogBox
				open={isUpdateTierDialogOpen}
				dialogTitle={intlService.GetFormattedMessage('usps-tier-config.tier-config-label')}
				dialogDescription={
					props.uspsTierConfig.selected?.tier.toLowerCase() === environment.USPSFulfillmentTierName?.toLowerCase() ? (
						<>
							{intlService.GetFormattedMessage('usps-tier-config-tier-fullfillment-dialog-title')}
							<br />
							<br />
							{intlService.GetFormattedMessage('usps-tier-config.config-edit-dialog-description')}{' '}
							{intlService.GetFormattedMessage('app.common.do-you-want-to-continue-text')}
						</>
					) : (
						<>
							{intlService.GetFormattedMessage('usps-tier-config.config-edit-dialog-description')}{' '}
							{intlService.GetFormattedMessage('app.common.do-you-want-to-continue-text')}
						</>
					)
				}
				closeCustomDialog={async (dialogString?: string, isCancel?: boolean) => {
					if (!isCancel) {
						props.updateUSPSTierConfig();
					}
					setIsUpdateTierDialogOpen(false);
				}}
				dialogYesButton={intlService.GetFormattedMessage('app.usps-tier-config-default-yes-label')}
				dialogNoButton={intlService.GetFormattedMessage('app.usps-tier-config-default-no-label')}
			/>
			{props.isPartnerAccount ? getUSPSConfigComponentForPartner() : getUSPSConfigComponentForCustomer()}
		</>
	);
};

interface IAdminAccountTierAndOverrideRateSetProps
	extends Pick<
		ICustomerAccountDetailsState,
		'account' | 'uspsTierConfigs' | 'uspsTierConfig' | 'buttonLoading' | 'uspsConfigdataLoading' | 'partnerUSPSTier'
	> {
	isPartnerAccount: boolean;
	onSetUSPSTierConfig: (tierConfig: models.IUSPSTierConfig) => void;
	updateUSPSTierConfig: () => void;
	account: IAccount;
}

export default AdminAccountUSPSTierComponent;



// WEBPACK FOOTER //
// ./src/components/admin/customer/admin-account-usps-tier.component.tsx